import { FetchResult, MutationFunction } from '@apollo/client';
import { DataValue, graphql } from '@apollo/client/react/hoc';
import gql from 'graphql-tag';
import { flowRight as compose } from 'lodash';
import React from 'react';
import { RouteComponentProps } from 'react-router-dom';

import { ErrorBoundary } from 'src/app/components/Error/ErrorBoundary';
import { PopupContainer } from 'src/app/components/Popup/PopupContainer';
import { PopupLogin } from 'src/app/components/Popup/PopupLogin';
import { HOMEPAGE, LOGIN_SUCCESS_QUERY } from 'src/app/constants/route';
import { EventTrack, pushEventTracking } from 'src/app/helpers/analytics';
import { getFullAppUrl, withConfig } from 'src/app/helpers/config';
import { setBodyModal } from 'src/app/helpers/dom';
import { withRouter } from 'src/app/helpers/router';
import { InjectedConfigProps } from 'src/app/models/config';
import { InjectedLoginPopupProps, InjectedLoginRedirectProps } from 'src/app/models/login';

export function withLoginRedirect(): <C>(WrappedComponent: C) => C {
  function wrapperComponent<C>(WrappedComponent: C): C;
  function wrapperComponent<P extends InjectedConfigProps & InjectedLoginRedirectProps>(WrappedComponent: React.ComponentType<P>): React.ComponentClass<Pick<P, Exclude<keyof P, keyof InjectedLoginRedirectProps>>> {
    const GRAPHQL_LOGIN_MUTATION = gql`
      mutation login($lastPageUrl: String!) {
        login (lastPageUrl: $lastPageUrl)
      }
    `;

    interface MyLoginResult {
      login: string;
    }

    interface MyInjectedProps {
      loginMutation: MutationFunction<MyLoginResult>;
    }

    type MyOwnProps = RouteComponentProps;

    type MyProps = MyOwnProps & MyInjectedProps;

    @compose([
      graphql(GRAPHQL_LOGIN_MUTATION, { name: 'loginMutation' })
    ])
    @withConfig()
    @withRouter()
    class MyClassWithLoginRedirect extends React.Component<P & MyProps> {
      constructor(props: P & MyProps) {
        super(props);

        this.login = this.login.bind(this);
      }

      render(): JSX.Element {
        return (
          <WrappedComponent {...this.props} openLoginPage={this.login} />
        );
      }

      private login(loginFrom: string, isNewTab = false, eventTrack?: EventTrack): void {
        const { config, loginMutation, location } = this.props;
        if (eventTrack) {
          pushEventTracking(
            eventTrack['eventDetails.category'],
            eventTrack['eventDetails.action'],
            eventTrack['eventDetails.label'],
            eventTrack.custom
          );
        }
        loginMutation({
          variables: {
            lastPageUrl: getFullAppUrl(config, location.pathname, this.combineQuery(location.search, 'med=login', `ref=${loginFrom}`, LOGIN_SUCCESS_QUERY))
          }
        }).then((result: FetchResult<MyLoginResult>) => {
          if (result && result.data) {
            if (isNewTab) {
              window.open(result.data.login, '_blank');
            } else {
              window.location.assign(result.data.login);
            }
          }
        });
      }

      private combineQuery(...queries: string[]): string {
        const builtQuery = queries.filter(Boolean).map((query) => query.charAt(0) === '?' ? query.substring(1) : query).join('&');
        return builtQuery ? '?' + builtQuery : '';
      }
    }

    return MyClassWithLoginRedirect;
  }

  return wrapperComponent;
}

export function withLoginPopup(): <C>(WrappedComponent: C) => C {
  function wrapperComponent<C>(WrappedComponent: C): C;
  function wrapperComponent<P extends InjectedLoginPopupProps>(WrappedComponent: React.ComponentType<P>): React.ComponentClass<Pick<P, Exclude<keyof P, keyof InjectedLoginPopupProps>>> {
    type MyInjectedProps = InjectedLoginRedirectProps;

    type MyOwnProps = RouteComponentProps;

    type MyProps = MyOwnProps & MyInjectedProps;

    interface MyState {
      isLoginPopupOpen: boolean;
      loginFrom: string;
      eventTrack?: EventTrack;
    }

    @withLoginRedirect()
    class MyClassWithLoginPopup extends React.Component<P & MyProps, MyState> {
      constructor(props: P & MyProps) {
        super(props);

        this.state = {
          isLoginPopupOpen: false,
          loginFrom: ''
        };

        this.openPopup = this.openPopup.bind(this);
        this.closePopup = this.closePopup.bind(this);
      }

      render(): JSX.Element {
        return (
          <>
            {this.renderPopupContainer()}
            <ErrorBoundary>
              <WrappedComponent {...this.props} openLoginPopup={this.openPopup} openLoginPage={this.props.openLoginPage} />
            </ErrorBoundary>
          </>
        );
      }

      private renderPopupContainer(): JSX.Element | null {
        if (this.state.isLoginPopupOpen) {
          return (
            <PopupContainer onClick={this.closePopup}>
              <PopupLogin login={this.props.openLoginPage} cancel={this.closePopup} loginFrom={this.state.loginFrom} eventTrack={this.state.eventTrack}/>
            </PopupContainer>
          );
        } else {
          return null;
        }
      }

      // Because our popup login always open in the current page, isNewPage args will be ignore for now
      private openPopup(loginFrom: string, isNewPage = false, eventTrack?: EventTrack): void {
        setBodyModal(true);
        this.setState({ isLoginPopupOpen: true, loginFrom, eventTrack });
      }

      private closePopup(): void {
        setBodyModal(false);
        this.setState({ isLoginPopupOpen: false });
      }
    }

    return MyClassWithLoginPopup;
  }

  return wrapperComponent;
}

export function withAuthorization(loginFrom: string, eventTrack?: EventTrack): <C>(WrappedComponent: C) => C {
  function wrappedComponent<C>(WrappedComponent: C): C;
  function wrappedComponent<P>(WrappedComponent: React.ComponentType<P>): React.ComponentClass<P> {
    interface MyQueryResult {
      user: any;
    }

    interface MyInjectedProps extends InjectedLoginRedirectProps {
      data: DataValue<MyQueryResult>;
    }

    type MyOwnProps = RouteComponentProps;

    type MyProps = MyOwnProps & MyInjectedProps;

    @withLoginRedirect()
    class MyClassWithAuthorization extends React.Component<P & MyProps> {
      constructor(props: P & MyProps) {
        super(props);

        this.cancelLogin = this.cancelLogin.bind(this);
      }

      render(): JSX.Element {
        const { user, loading, error } = this.props.data;

        if (user || loading || error) {
          return <WrappedComponent {...this.props} />;
        }
        return (
          <PopupContainer>
            <PopupLogin login={this.props.openLoginPage} cancel={this.cancelLogin} loginFrom={loginFrom} eventTrack={eventTrack}/>
          </PopupContainer>
        );
      }

      private cancelLogin(): void {
        this.props.history.push(HOMEPAGE);
      }
    }

    return MyClassWithAuthorization;
  }

  return wrappedComponent;
}
